/**
 * <bigens-pdp-closing> (T33 → T45): the "Ready to bounce?" band at the end of the product page.
 *
 * - Every time the band comes near the viewport, it is refreshed from the buy box: the pick line (the set builder's
 *   summary, else the checked options of Horizon's variant picker, else the product title) and the add-to-cart button,
 *   which mirrors the buy box's own button ("Add 2 balls to cart" + the total in its chip). No usable button
 *   (waitlist, sold out) = no add button and no price; "Back to your ball" becomes the primary button.
 * - "Add to cart" clicks the buy box's add-to-cart button (a puppet click, no fly-to-cart animation), so the same
 *   product form and the builder's getCartItems() add the whole set in one request (AUD2 #10).
 * - "Back to your ball" scrolls back to the product details (instantly under reduced motion) and moves focus to the
 *   title, so keyboard and screen-reader users land in the same place. Without JS it's a link to #MainContent.
 * No copy in JS: everything visible is rendered in Liquid or read from the page.
 */
class BigensPdpClosing extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | undefined} */
  #puppetTimer;

  connectedCallback() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#refresh();
      },
      { rootMargin: '200px 0px' },
    );
    this.#observer.observe(this);
    this.querySelector('[data-closing-button]')?.addEventListener('click', this.#onBack);
    this.querySelector('[data-closing-atc]')?.addEventListener('click', this.#onAdd);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.querySelector('[data-closing-button]')?.removeEventListener('click', this.#onBack);
    this.querySelector('[data-closing-atc]')?.removeEventListener('click', this.#onAdd);
    window.clearTimeout(this.#puppetTimer);
  }

  /** The main product column (Horizon's product-information section). */
  #buyBox() {
    return document.querySelector('.product-information .product-details') ?? document.querySelector('.product-information');
  }

  /** The buy box's own add-to-cart button, when the shopper can use it. */
  #mainButton() {
    const button = /** @type {HTMLButtonElement | null} */ (
      this.#buyBox()?.querySelector('product-form-component button[name="add"], button[name="add"]') ?? null
    );
    if (!button || button.disabled || button.closest('[hidden]') || button.offsetParent === null) return null;
    return button;
  }

  #refresh() {
    const buyBox = this.#buyBox();
    if (!buyBox) return;
    const button = this.#mainButton();

    const pick = this.querySelector('[data-closing-pick]');
    if (pick) {
      const builder = /** @type {any} */ (buyBox.querySelector('product-deal-builder:not([hidden])'));
      const summary = typeof builder?.summaryText === 'function' && button ? builder.summaryText() : '';
      const options = Array.from(buyBox.querySelectorAll('variant-picker fieldset input:checked, variant-picker select'))
        .map((input) => /** @type {HTMLInputElement | HTMLSelectElement} */ (input).value)
        .filter(Boolean);
      pick.textContent = summary || (options.length ? options.join(', ') : this.dataset.defaultPick ?? pick.textContent);
    }

    const buttonPrice = button?.querySelector('.add-to-cart__price')?.textContent?.trim() || '';

    const add = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-closing-atc]'));
    const back = this.querySelector('[data-closing-button]');
    if (add) {
      add.hidden = !button;
      const label = this.querySelector('[data-closing-atc-label]');
      const title = button?.querySelector('.add-to-cart-text__title');
      const titleText = title
        ? Array.from(title.childNodes)
            .filter((node) => node.nodeType === Node.TEXT_NODE)
            .map((node) => node.textContent || '')
            .join('')
            .trim()
        : '';
      if (label && titleText) label.textContent = titleText;
      const total = this.querySelector('[data-closing-atc-total]');
      if (total instanceof HTMLElement) {
        total.textContent = buttonPrice;
        total.hidden = !buttonPrice;
      }
    }
    // Without an add button the link is the band's one action: a filled button; otherwise a quiet text link.
    back?.classList.toggle('button', !button);
  }

  #onAdd = () => {
    const button = this.#mainButton();
    if (!button) {
      this.#refresh();
      return;
    }
    button.dataset.puppet = 'true';
    button.click();
    window.clearTimeout(this.#puppetTimer);
    this.#puppetTimer = window.setTimeout(() => {
      button.dataset.puppet = 'false';
    }, 1500);
  };

  /** @param {MouseEvent} event */
  #onBack = (event) => {
    const buyBox = this.#buyBox();
    if (!buyBox) return;
    event.preventDefault();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Clear the sticky header (Horizon keeps --header-height on <body>).
    if (buyBox instanceof HTMLElement) buyBox.style.scrollMarginTop = 'calc(var(--header-height, 0px) + 1rem)';
    buyBox.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });

    const title = /** @type {HTMLElement | null} */ (buyBox.querySelector('h1'));
    if (title) {
      if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
    }
  };
}

if (!customElements.get('bigens-pdp-closing')) {
  customElements.define('bigens-pdp-closing', BigensPdpClosing);
}
